import axios from "axios";
import { baseURL } from "../utils/StaticVariables";

/**
 * Normalizes the base API URL to ensure a trailing slash.
 */
const getApiBaseUrl = () => {
  const url = baseURL || "/";
  return url.endsWith("/") ? url : `${url}/`;
};

/**
 * Fetches all cameras for No Entry Zone.
 * Endpoint: GET /no-entry-zone/cameras
 *
 * @param {string} token - Bearer auth token
 * @returns {Promise<Array>} List of cameras
 */
export const fetchNoEntryCameras = async (token) => {
  const url = `${getApiBaseUrl()}no-entry-zone/cameras`;
  const response = await axios.get(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  return response.data;
};

/**
 * Fetches existing polygon configuration for a given camera stream.
 * Endpoint: GET /no-entry-zone/cameras/{stream_id}/polygon
 *
 * @param {string} streamId - Camera stream UUID
 * @param {string} token - Bearer auth token
 * @returns {Promise<Object>} Polygon response data
 */
export const fetchCameraPolygon = async (streamId, token) => {
  if (!streamId) {
    throw new Error("Stream ID is required to fetch polygon");
  }
  const url = `${getApiBaseUrl()}no-entry-zone/cameras/${encodeURIComponent(streamId)}/polygon`;
  const response = await axios.get(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  return response.data;
};

/**
 * Persists or updates the polygon configuration for a camera stream.
 * Endpoint: PUT /no-entry-zone/cameras/{stream_id}/polygon
 *
 * @param {string} streamId - Camera stream UUID
 * @param {Object} payload - { polygon: [[x, y], ...], ref_width: number, ref_height: number, is_active: boolean }
 * @param {string} token - Bearer auth token
 * @returns {Promise<Object>} Updated polygon response
 */
export const saveCameraPolygon = async (streamId, payload, token) => {
  if (!streamId) {
    throw new Error("Stream ID is required to save polygon");
  }
  const url = `${getApiBaseUrl()}no-entry-zone/cameras/${encodeURIComponent(streamId)}/polygon`;
  const response = await axios.put(url, payload, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * Fetches incidents per camera analytics.
 * Endpoint: GET /no-entry-zone/analytics/incidents-per-camera
 *
 * @param {Object} params - Query filters (camera_id, start_date, end_date, etc.)
 * @param {string} token - Bearer auth token
 * @returns {Promise<Array>} Analytics data list
 */
export const fetchNoEntryIncidentsPerCamera = async (params = {}, token) => {
  const url = `${getApiBaseUrl()}no-entry-zone/analytics/incidents-per-camera`;
  const response = await axios.get(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
    params,
  });
  return response.data;
};

/**
 * Fetches model confidence audit analytics.
 * Endpoint: GET /no-entry-zone/analytics/confidence-audit
 *
 * @param {Object} params - Query filters
 * @param {string} token - Bearer auth token
 * @returns {Promise<Object>} Object with summary, per_camera, histogram, per_event
 */
export const fetchNoEntryConfidenceAudit = async (params = {}, token) => {
  const url = `${getApiBaseUrl()}no-entry-zone/analytics/confidence-audit`;
  const response = await axios.get(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
    params,
  });
  return response.data;
};

/**
 * Fetches a snapshot (first frame) image from a camera stream.
 * Endpoint: GET /no-entry-zone/cameras/{camera_id}/snapshot
 *
 * @param {string} cameraId - Camera stream UUID
 * @param {string} token - Bearer auth token
 * @returns {Promise<string>} Object URL of the snapshot blob
 */
export const fetchCameraSnapshot = async (cameraId, token) => {
  if (!cameraId) {
    throw new Error("Camera ID is required to fetch snapshot");
  }
  const url = `${getApiBaseUrl()}no-entry-zone/cameras/${encodeURIComponent(cameraId)}/snapshot`;
  const response = await axios.get(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
    responseType: "blob",
  });
  return URL.createObjectURL(response.data);
};
